import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { observer } from 'mobx-react';
import { parse, stringify } from 'query-string';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { v4 as uuid } from 'uuid';
import { useEventLogging } from '../../../models/Logging';
import { useActionItemSummaryQuery } from '../../../queries/ActionItem/useActionItemSummaryQuery';
import { useActionItemComposer, useNoteComposer, useUserSession } from '../../../models/hooks/appStateHooks';
import { ActionItemViewModel, NoteViewModel } from '../../../viewmodels/AppViewModels';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { Portal, PortalDestination } from '../../components/Portal';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../components/Tabs';
import { ActionItemsIcon } from '../../components/svgs/icons/ActionItemsIcon';
import { NotesIcon } from '../../components/svgs/icons/NotesIcon';
import { THEME } from '../../components-v2/Theme/theme';
import { NotesList } from '../../components/notes/NotesList';
import { ActionItemsPage } from '../../pages/ActionItems';
import { baseStyleSheet } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

export enum NotesActionItemsSelectedTabOption {
	Notes = 'notes',
	ActionItems = 'action-items',
	/** Legacy tab; now redirects to the Action Items tab's completed view. */
	Completed = 'completed',
}

interface IProps {
	className?: string;
}

function NotesActionItems({ className }: IProps) {
	const scrollToBottomPortalId = React.useRef(uuid());
	const location = useLocation<any>();
	const history = useHistory();
	const userSession = useUserSession();
	const noteComposer = useNoteComposer();
	const actionItemComposer = useActionItemComposer();
	const { logEvent, logApiError } = useEventLogging('NotesActionItems');
	const { data: actionItemSummary } = useActionItemSummaryQuery();
	const overdueActionItems = actionItemSummary?.overdue ?? 0;
	const searchParams = parse(location.search);
	const activeTab =
		(searchParams.activeTab as NotesActionItemsSelectedTabOption | null) ?? NotesActionItemsSelectedTabOption.Notes;
	const actionItemId = searchParams.actionItemId;
	const noteId = searchParams.noteId;
	// When the tab changes, keep all the rest of the query params intact and just change the activeTab
	const redirectToTab = React.useCallback(
		(tab: NotesActionItemsSelectedTabOption) => {
			if (!history) {
				return;
			}
			const url = `/notes-action-items?${stringify({ ...searchParams, activeTab: tab })}`;
			history.replace(url);
		},
		[history, searchParams]
	);

	React.useEffect(() => {
		if (activeTab === NotesActionItemsSelectedTabOption.Completed) {
			history.replace(
				`/notes-action-items?${stringify({
					...searchParams,
					activeTab: NotesActionItemsSelectedTabOption.ActionItems,
					view: 'completed',
				})}`
			);
		}
	}, [activeTab, history, searchParams]);

	const closeModal = (sParamKey: string) => () => {
		if (!history || searchParams[sParamKey] == null) {
			return;
		}
		const sParams = { ...searchParams };
		delete sParams[sParamKey];
		history.replace(`/notes-action-items?${stringify(sParams)}`);
	};
	const closeNoteModal = closeModal('noteId');
	const closeActionItemModal = closeModal('actionItemId');

	React.useEffect(() => {
		const load = async () => {
			const noteVm = new NoteViewModel(userSession, { id: noteId });
			logEvent('LoadNote', noteVm);
			try {
				await noteVm.load();

				noteComposer.show(noteVm, closeNoteModal);
			} catch (error) {
				logApiError('LoadNote', error);
			}
		};
		if (noteId) {
			load();
		}
		return () => {
			if (noteId == null) {
				noteComposer.reset();
			}
		};
	}, [noteId, noteComposer, actionItemComposer, logEvent, logApiError, userSession, closeNoteModal]);
	React.useEffect(() => {
		const load = async () => {
			const actionItemVm = new ActionItemViewModel(userSession, { id: actionItemId } as Api.IActionItem);
			logEvent('LoadActionItem', actionItemVm);
			try {
				await actionItemVm.load();

				actionItemComposer.show(actionItemVm, closeActionItemModal);
			} catch (error) {
				logApiError('LoadActionItem', error);
			}
		};
		if (actionItemId) {
			load();
		}
		return () => {
			if (actionItemId == null) {
				actionItemComposer.reset();
			}
		};
	}, [actionItemId, noteComposer, actionItemComposer, logEvent, logApiError, userSession, closeActionItemModal]);
	const handleEditNote = (id: string) => {
		const newSearchParams = { ...searchParams, noteId: id };
		history.replace(`/notes-action-items?${stringify(newSearchParams)}`);
	};

	const selectedTab =
		activeTab === NotesActionItemsSelectedTabOption.Completed
			? NotesActionItemsSelectedTabOption.ActionItems
			: activeTab;

	return (
		<div className={`${css(styleSheet.container)} notes-container ${className || ''}`}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Notes & Actions</h1>
			</Portal>
			<TabRoot value={selectedTab} onValueChange={tab => redirectToTab(tab as NotesActionItemsSelectedTabOption)}>
				<TabList>
					<IconTabTrigger icon={<NotesIcon fillColor='currentColor' />} value={NotesActionItemsSelectedTabOption.Notes}>
						Notes
					</IconTabTrigger>
					<IconTabTrigger
						icon={<ActionItemsIcon fillColor='currentColor' />}
						value={NotesActionItemsSelectedTabOption.ActionItems}
					>
						Action Items
						{overdueActionItems > 0 ? (
							<span className={css(viewStyleSheet.tabBadge)} aria-label={`${overdueActionItems} overdue action items`}>
								{overdueActionItems > 99 ? '99+' : overdueActionItems}
							</span>
						) : null}
					</IconTabTrigger>
				</TabList>
				<TabContent value={NotesActionItemsSelectedTabOption.Notes} className={css(viewStyleSheet.content)}>
					<NotesList scrollToBottomPortalId={scrollToBottomPortalId.current} onEditNote={handleEditNote} />
				</TabContent>
				<TabContent
					value={NotesActionItemsSelectedTabOption.ActionItems}
					className={css(viewStyleSheet.content, viewStyleSheet.tab)}
				>
					<ActionItemsPage />
				</TabContent>
			</TabRoot>
			<PortalDestination id={scrollToBottomPortalId.current} />
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
}

const CONTENT_MAX_WIDTH = 960;

const viewStyleSheet = StyleSheet.create({
	tabBadge: {
		alignItems: 'center',
		backgroundColor: THEME.colors.danger.main,
		borderRadius: 9,
		boxSizing: 'border-box',
		color: THEME.colors.background.light,
		display: 'inline-flex',
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		height: 18,
		justifyContent: 'center',
		marginLeft: THEME.spacing.xs,
		minWidth: 18,
		padding: '0 5px',
	},
	content: {
		boxSizing: 'border-box',
		maxWidth: CONTENT_MAX_WIDTH,
		paddingTop: THEME.spacing.md,
		width: '100%',
	},
	tab: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		minWidth: 0,
	},
});

export const NoteActionItemsContainer = observer(NotesActionItems);
